import React from 'react'
import { StyleSheet, View } from 'react-native'
import { Card, Text, Chip } from 'react-native-paper'
import { useNavigation } from '@react-navigation/native'
import { AppNavigation } from '../../../../navigation/types'
import {
  NodeTopContributionsQuery,
  ContributionType,
} from '../../../../generated/graphql'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../../../hooks/theme.hook'

interface ContributionStatRowProps {
  contribution: NodeTopContributionsQuery['nodeTopContributions']['contributions'][0]
  rank: number
}

const contributionTypeLabels: Record<ContributionType, string> = {
  [ContributionType.Edit]: 'Edit',
  [ContributionType.Inquiry]: 'Inquiry',
  [ContributionType.Challenge]: 'Challenge',
  [ContributionType.Reserved]: 'Reserved',
  [ContributionType.OwnerEdit]: 'Owner Edit',
}

export function ContributionStatRow({
  contribution,
  rank,
}: ContributionStatRowProps) {
  const navigation = useNavigation<AppNavigation>()
  const { t } = useTranslation('common')
  const theme = useTheme()

  const handlePress = () => {
    navigation.navigate('Perspective', { articleId: contribution.articleId })
  }

  // Get contribution type color
  const contributionTypeColors: Record<ContributionType, string> = {
    [ContributionType.Reserved]: theme.colors.editBkg,
    [ContributionType.Edit]: theme.colors.editBkg,
    [ContributionType.OwnerEdit]: theme.colors.editBkg,
    [ContributionType.Challenge]: theme.colors.challengeBkg,
    [ContributionType.Inquiry]: theme.colors.inquiryBkg,
  }

  const chipColor =
    contributionTypeColors[contribution.contributionType] ||
    theme.colors.surface

  // Get contribution info text - use motivation or input
  const contributionInfo =
    contribution.motivation ||
    contribution.input ||
    contribution.originalText ||
    'N/A'
  const displayInfo =
    contributionInfo.length > 100
      ? contributionInfo.substring(0, 100) + '...'
      : contributionInfo

  return (
    <Card style={styles.card} onPress={handlePress}>
      <Card.Content style={styles.content}>
        <View style={styles.rankSection}>
          <Text variant="labelLarge" style={styles.rankText}>
            #{rank}
          </Text>
        </View>
        <View style={styles.mainSection}>
          <View style={styles.chipContainer}>
            <Chip
              style={[styles.typeChip, { backgroundColor: chipColor }]}
              textStyle={styles.chipText}
              compact
            >
              {contributionTypeLabels[contribution.contributionType] ||
                contribution.contributionType}
            </Chip>
          </View>
          <Text variant="bodyMedium" style={styles.info} numberOfLines={2}>
            {displayInfo}
          </Text>

          <Text variant="bodySmall" style={styles.date}>
            {new Date(contribution.createdAt).toISOString().split('T')[0]}
          </Text>
        </View>
        <View style={styles.statsSection}>
          <Text variant="bodySmall" style={styles.label}>
            {t('Bump')}
          </Text>
          <Text variant="titleLarge">{contribution.bumpScore || 0}</Text>
        </View>
      </Card.Content>
    </Card>
  )
}

const styles = StyleSheet.create({
  card: {
    marginBottom: 8,
  },
  content: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
  },
  rankSection: {
    marginRight: 12,
    minWidth: 40,
    alignItems: 'center',
  },
  rankText: {
    fontWeight: '600',
    textAlign: 'center',
  },
  mainSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
    gap: 8,
  },
  info: {
    fontWeight: '400',
  },
  chipContainer: {
    gap: 8,
  },
  typeChip: {
    alignSelf: 'flex-start',
  },
  clusterChip: {
    alignSelf: 'flex-start',
  },
  chipText: {
    fontSize: 11,
  },
  date: {
    opacity: 0.6,
    marginTop: 4,
  },
  statsSection: {
    alignItems: 'center',
    minWidth: 60,
  },
  label: {
    opacity: 0.6,
    marginBottom: 4,
  },
})
